import { axiosInstance } from '@/common/plugins/axios'
import API_ENDPOINTS from '@/common/constants/api'
import type { App } from 'vue'

export type ErrorReportContext = Record<string, string>

export type ErrorBreadcrumb = {
  t: number
  type: string
  id?: string
}

export type ClientErrorPayload = {
  message: string
  name?: string
  stack?: string
  fingerprint: string
  url: string
  release: string
  client: string
  environment: string
  userAgent: string
  sessionId?: string
  context?: ErrorReportContext
  breadcrumbs?: ErrorBreadcrumb[]
}

const MAX_MESSAGE = 2000
const MAX_STACK = 16000
const MAX_BREADCRUMBS = 10
const MAX_PER_MINUTE = 20
const DEDUP_WINDOW_MS = 8_000
const FLUSH_DEBOUNCE_MS = 300

const NOISE_PATTERNS: RegExp[] = [
  /ResizeObserver loop/i,
  /Script error\.?/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
]

let installed = false
let flushTimer: ReturnType<typeof setTimeout> | null = null
const queue: ClientErrorPayload[] = []
const recentFingerprints = new Map<string, number>()
const sendTimestamps: number[] = []
const breadcrumbs: ErrorBreadcrumb[] = []

function isReportingEnabled(): boolean {
  return import.meta.env.VITE_ERROR_REPORTING !== 'false'
}

function truncate(value: string | undefined | null, max: number): string {
  if (value == null) return ''
  return value.length <= max ? value : value.slice(0, max)
}

function sanitizeContext(context?: ErrorReportContext): ErrorReportContext | undefined {
  if (!context) return undefined
  const out: ErrorReportContext = {}
  let count = 0
  for (const [key, value] of Object.entries(context)) {
    if (count >= 16) break
    if (!key || typeof value !== 'string') continue
    out[truncate(key, 200)] = truncate(value, 200)
    count++
  }
  return Object.keys(out).length ? out : undefined
}

function resolveRelease(): string {
  return (
    (import.meta.env.VITE_APP_RELEASE as string | undefined) ||
    (import.meta.env.VITE_APP_VERSION as string | undefined) ||
    'dev'
  )
}

function resolveEnvironment(): string {
  return import.meta.env.MODE || 'production'
}

function toErrorParts(input: unknown): { name?: string; message: string; stack?: string } {
  if (input instanceof Error) {
    return {
      name: input.name,
      message: input.message || String(input),
      stack: input.stack,
    }
  }
  if (typeof input === 'string') {
    return { message: input }
  }
  if (input && typeof input === 'object') {
    const obj = input as Record<string, unknown>
    const message =
      typeof obj.message === 'string'
        ? obj.message
        : typeof obj.reason === 'string'
          ? obj.reason
          : String(input)
    const name = typeof obj.name === 'string' ? obj.name : undefined
    const stack = typeof obj.stack === 'string' ? obj.stack : undefined
    return { name, message, stack }
  }
  return { message: String(input) }
}

function topStackFrame(stack?: string): string {
  if (!stack) return ''
  const lines = stack.split('\n').map((l) => l.trim()).filter(Boolean)
  for (const line of lines) {
    if (/^(Error|TypeError|ReferenceError|SyntaxError)/i.test(line)) continue
    return line.slice(0, 200)
  }
  return ''
}

export function buildFingerprint(name: string | undefined, message: string, stack?: string): string {
  return truncate([name || 'Error', message, topStackFrame(stack)].join('|'), 500)
}

export function shouldIgnoreError(parts: { name?: string; message: string; stack?: string }): boolean {
  if (parts.name === 'AbortError') return true
  const blob = `${parts.name || ''} ${parts.message} ${parts.stack || ''}`
  if (NOISE_PATTERNS.some((re) => re.test(blob))) return true
  // Expected auth noise after logout / expired session
  if (/\b401\b/.test(parts.message) && /unauthorized|session|auth/i.test(parts.message)) {
    return true
  }
  return false
}

function pruneSendTimestamps(now: number): void {
  while (sendTimestamps.length && now - sendTimestamps[0] > 60_000) {
    sendTimestamps.shift()
  }
}

function canSend(now: number): boolean {
  pruneSendTimestamps(now)
  return sendTimestamps.length < MAX_PER_MINUTE
}

function isDuplicate(fingerprint: string, now: number): boolean {
  const prev = recentFingerprints.get(fingerprint)
  if (prev != null && now - prev < DEDUP_WINDOW_MS) {
    return true
  }
  recentFingerprints.set(fingerprint, now)
  for (const [fp, ts] of recentFingerprints) {
    if (now - ts > DEDUP_WINDOW_MS) recentFingerprints.delete(fp)
  }
  return false
}

export function pushBreadcrumb(type: string, id?: string): void {
  breadcrumbs.push({ t: Date.now(), type: truncate(type, 200), id: id ? truncate(id, 200) : undefined })
  while (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift()
  }
}

function scheduleFlush(): void {
  if (flushTimer != null) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flushQueue()
  }, FLUSH_DEBOUNCE_MS)
}

async function flushQueue(): Promise<void> {
  if (!queue.length || !isReportingEnabled()) {
    queue.length = 0
    return
  }
  while (queue.length) {
    const payload = queue.shift()
    if (!payload) break
    const now = Date.now()
    if (!canSend(now)) {
      // drop remaining to avoid backlog after rate limit
      queue.length = 0
      break
    }
    sendTimestamps.push(now)
    try {
      await axiosInstance.post(API_ENDPOINTS.clientErrors.url, payload, {
        // swallow: never trigger session-expired redirect / snackbars
        _skipGlobalErrorHandling: true,
        timeout: 5000,
        validateStatus: () => true,
      } as any)
    } catch {
      // fail-silent
    }
  }
}

export function reportError(input: unknown, context?: ErrorReportContext): void {
  if (!isReportingEnabled()) return

  const parts = toErrorParts(input)
  if (!parts.message) return
  if (shouldIgnoreError(parts)) return

  const fingerprint = buildFingerprint(parts.name, parts.message, parts.stack)
  const now = Date.now()
  if (isDuplicate(fingerprint, now)) return

  const payload: ClientErrorPayload = {
    message: truncate(parts.message, MAX_MESSAGE),
    name: parts.name ? truncate(parts.name, 200) : undefined,
    stack: parts.stack ? truncate(parts.stack, MAX_STACK) : undefined,
    fingerprint,
    url: truncate(typeof location !== 'undefined' ? location.href : '', 2000),
    release: resolveRelease(),
    client: String(import.meta.env.VITE_CLIENT || ''),
    environment: resolveEnvironment(),
    userAgent: truncate(typeof navigator !== 'undefined' ? navigator.userAgent : '', 500),
    context: sanitizeContext(context),
    breadcrumbs: breadcrumbs.slice(-MAX_BREADCRUMBS),
  }

  queue.push(payload)
  // keep queue small
  while (queue.length > 10) queue.shift()
  scheduleFlush()
}

export function installErrorReporter(app: App): void {
  if (installed || !isReportingEnabled()) return
  installed = true

  app.config.errorHandler = (err, _instance, info) => {
    console.error('[vue]', err, info)
    reportError(err, { source: 'vue', info: String(info || '') })
  }

  window.addEventListener('error', (event) => {
    if (event.error) {
      reportError(event.error, { source: 'window.onerror' })
      return
    }
    reportError(event.message || 'window.onerror', { source: 'window.onerror' })
  })

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' })
  })
}

/** @internal test helpers */
export const __errorReporterTest = {
  reset() {
    installed = false
    queue.length = 0
    recentFingerprints.clear()
    sendTimestamps.length = 0
    breadcrumbs.length = 0
    if (flushTimer != null) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
  },
  getQueue: () => queue.slice(),
}
